import { CONNECTOR } from "../../PCBHelper";
import { getSchematicPcbColor } from "./connectionPlot";
import { getInterfaceSetupInfo } from "./setupHelper";

class MultiPCBConnections {
  constructor() {
    this.connectionsData = new Map();
    this.interfaceSetup = new Map();
    this.unitSize = 40;
  }

  get = (verificationId) => {
    return this.connectionsData.get(verificationId);
  }

  getSetup = (verificationId) => {
    return this.interfaceSetup.get(verificationId);
  }

  initConnections = (interfaces) => {
    let edges = [];
    const interfaceInfo = {};
    const existComps = [];
    const interfaceInfoList = []
    for (let info of interfaces) {
      if (!info.content || !info.content.components || !info.content.components.length) {
        continue;
      }
      interfaceInfo[info.pcbId] = info;
      interfaceInfoList.push(getInterfaceSetupInfo(info));
    }

    for (let pcbId of Object.keys(interfaceInfo)) {
      const info = interfaceInfo[pcbId];
      const connectorComps = info.content.components.filter(comp => comp.type === CONNECTOR);
      for (let comp of connectorComps) {
        if (!comp.connect || !comp.connect.pcbId || !comp.connect.componentName) {
          continue;
        }
        if (existComps.includes(`${info.pcbId}::${comp.name}`)) {
          continue;
        }
        const connPCB = interfaceInfo[comp.connect.pcbId];
        if (!connPCB || !connPCB.content || !connPCB.content.components || !connPCB.content.components.length) {
          continue;
        }
        const connComp = connPCB.content.components.find(item => item.type === CONNECTOR && item.name === comp.connect.componentName);
        if (!connComp) {
          continue;
        }
        const connPins = connComp.pins || [];
        const findItem = edges.find(item => [`${info.pcbId}::${comp.name}::${connPCB.pcbId}::${comp.connect.componentName}`, `${connPCB.pcbId}::${comp.connect.componentName}::${info.pcbId}::${comp.name}`].includes(item.id))
        if (findItem) {
          continue;
        }
        edges.push({
          id: `${info.pcbId}::${comp.name}::${connPCB.pcbId}::${comp.connect.componentName}`,
          startPCB: info.pcbId,
          endPCB: connPCB.pcbId,
          startPCBName: info.pcb,
          endPCBName: connPCB.pcb,
          start: comp.name,
          end: comp.connect.componentName,
          pinsConnections: comp.pins.map(pinItem => {
            let pinMap = pinItem.pin_map, connPin = connPins.find(pin => pin.pin === pinItem.pin_map);
            if (!pinMap) {
              connPin = connPins.find(pin => pin.pin_map === pinItem.pin) || {};
              pinMap = connPin.pin;
            }
            return {
              start: pinItem.pin,
              end: pinMap,
              startSignal: pinItem.signal,
              endSignal: connPin.signal,
              startNet: pinItem.net,
              endNet: connPin.net,
            }
          })
        })
      }
    }

    return { edges, interfaceInfo, interfaceInfoList };
  }

  getPCBConnections = (verificationId, interfaces) => {
    const { edges, interfaceInfo, interfaceInfoList } = this.initConnections(interfaces);
    this.interfaceSetup.set(verificationId, interfaceInfoList);
    // calculate pcb path and layout
    const pathResult = this.calculatePath(edges);
    const pcbConnectors = this.calculatePCBConnectors(pathResult, edges);
    const sortedConnectors = this.sortConnectorsByDistance(pcbConnectors, pathResult, edges);
    const layout = this.generateLayout({ pathResult, sortedConnectors, edges, interfaceInfo });
    // calculate Bounds
    this.calculateBounds(layout);

    this.connectionsData.set(verificationId, layout);

    return layout;
  }
  // calculate PCB path
  calculatePath = (edges) => {
    const pcbConnections = new Map();
    const connections = new Map();

    //Count the number of connections for each PCB
    for (let edge of edges) {
      [edge.startPCB, edge.endPCB].forEach(pcb => {
        if (!pcbConnections.has(pcb)) {
          pcbConnections.set(pcb, 0);
          connections.set(pcb, new Set());
        }
        pcbConnections.set(pcb, pcbConnections.get(pcb) + edge.pinsConnections.length);
      });

      connections.get(edge.startPCB).add(edge.endPCB);
      connections.get(edge.endPCB).add(edge.startPCB);
    };

    /*    // Select the PCB with the most connections as the center.
       let centerPCB = null;
       let maxConnections = -1;
       for (const [pcb, count] of pcbConnections) {
         if (count > maxConnections) {
           maxConnections = count;
           centerPCB = pcb;
         }
       }
   
       // BFS layout based on the central PCB
       const levels = new Map();
       const queue = [{ pcb: centerPCB, level: 0 }];
       const visited = new Set([centerPCB]); */
    // Find the nodes with a degree of 1 as the start and end points.
    let startPCB = null;
   /*  let endPCB = null; */
    const endpoints = [];

    for (const [pcb, neighbors] of connections) {
      if (neighbors.size === 1) {
        endpoints.push(pcb);
      }
    }

    if (endpoints.length >= 1) {
      startPCB = endpoints[0];
    }
   /*  if (endpoints.length >= 2) {
      endPCB = endpoints[1];
    } */

    //if the nodes with a degree of 1 has not found, choose the pcb with least connection
    if (!startPCB) {
      let minConnections = Infinity;
      for (const [pcb, neighbors] of connections) {
        if (neighbors.size < minConnections) {
          minConnections = neighbors.size;
          startPCB = pcb;
        }
      }
    }
    // BFS calc pcb level
    const levels = new Map();
    const queue = [{ pcb: startPCB, level: 0 }];
    const visited = new Set([startPCB]);

    while (queue.length > 0) {
      const current = queue.shift();
      levels.set(current.pcb, current.level);

      const neighbors = Array.from(connections.get(current.pcb) || []);
      // sort neighbors by connection count
      neighbors.sort((a, b) => pcbConnections.get(b) - pcbConnections.get(a));

      neighbors.forEach(neighbor => {
        if (!visited.has(neighbor)) {
          queue.push({ pcb: neighbor, level: current.level + 1 });
          visited.add(neighbor);
        }
      });
    }

    //sort by level
    const sortedPCBs = Array.from(pcbConnections.keys()).sort((a, b) => {
      return (levels.get(a) || 0) - (levels.get(b) || 0);
    });

    return {
      path: sortedPCBs,
      levels: levels,
      connections
    };
  }

  // 2. Calculate the connector layout for each PCB
  calculatePCBConnectors = (pathResult, edges) => {
    const pcbConnectors = {};
    const { path, levels } = pathResult;

    for (let pcb of path) {
      pcbConnectors[pcb] = {
        left: new Map(),
        right: new Map()
      };
    }

    for (let edge of edges) {

      const startLevel = levels.get(edge.startPCB) || 0;
      const endLevel = levels.get(edge.endPCB) || 0;

      const startId = `${edge.startPCB}::${edge.start}`, endId = `${edge.endPCB}::${edge.end}`;

      if (startLevel < endLevel) {
        // left to right
        if (!pcbConnectors[edge.startPCB].right.has(edge.start)) {
          pcbConnectors[edge.startPCB].right.set(edge.start, []);
        }
        pcbConnectors[edge.startPCB].right.get(edge.start).push(endId);

        if (!pcbConnectors[edge.endPCB].left.has(edge.end)) {
          pcbConnectors[edge.endPCB].left.set(edge.end, []);
        }
        pcbConnectors[edge.endPCB].left.get(edge.end).push(startId);

      } else {
        // right to left
        if (!pcbConnectors[edge.startPCB].left.has(edge.start)) {
          pcbConnectors[edge.startPCB].left.set(edge.start, []);
        }
        pcbConnectors[edge.startPCB].left.get(edge.start).push(endId);

        if (!pcbConnectors[edge.endPCB].right.has(edge.end)) {
          pcbConnectors[edge.endPCB].right.set(edge.end, []);
        }
        pcbConnectors[edge.endPCB].right.get(edge.end).push(startId);
      }
    };

    return pcbConnectors;
  }

  // calc pcb connector by pcb level
  sortConnectorsByDistance = (pcbConnectors, pathResult, edges) => {
    const { path, levels } = pathResult;
    const sortedConnectors = {};

    path.forEach(pcb => {
      sortedConnectors[pcb] = { left: new Map(), right: new Map() };

      const leftConnectors = Array.from(pcbConnectors[pcb].left.entries());
      const rightConnectors = Array.from(pcbConnectors[pcb].right.entries());

      // calc distance (connector pcb -> target pcb)
      const calculateConnectorDistance = (connectorName, side) => {
        let minDistance = Infinity;

        edges.forEach(edge => {
          if ((edge.startPCB === pcb && edge.start === connectorName && side === 'right') ||
            (edge.endPCB === pcb && edge.end === connectorName && side === 'left')) {

            const targetPCB = edge.startPCB === pcb ? edge.endPCB : edge.startPCB;
            const distance = Math.abs(levels.get(pcb) - levels.get(targetPCB));
            minDistance = Math.min(minDistance, distance);
          }
        });

        return minDistance;
      };

      // sort by distance left
      leftConnectors.sort(([nameA], [nameB]) => {
        const distA = calculateConnectorDistance(nameA, 'left');
        const distB = calculateConnectorDistance(nameB, 'left');
        return distA - distB;
      });

      //  sort by distance right
      rightConnectors.sort(([nameA], [nameB]) => {
        const distA = calculateConnectorDistance(nameA, 'right');
        const distB = calculateConnectorDistance(nameB, 'right');
        return distA - distB;
      });

      //new connections
      for (let [name, connComps] of leftConnectors) {
        sortedConnectors[pcb].left.set(name, connComps);
      }
      for (let [name, connComps] of rightConnectors) {
        sortedConnectors[pcb].right.set(name, connComps);
      }
    });

    return sortedConnectors;
  }

  generateLayout = ({ pathResult, sortedConnectors, edges, interfaceInfo }) => {
    const layout = {
      pcbs: {},
      connections: new Map(),
      paths: []
    };
    const { path } = pathResult;
    const levelWidth = 9;
    const baseY = 1;
    const pcbWidth = 6;
    const compWidth = 2;
    let pcbExistColor = []

    path.forEach((pcbId, index) => {
      let color = getSchematicPcbColor(pcbExistColor);
      pcbExistColor.push(color);
      layout.pcbs[pcbId] = {
        name: interfaceInfo[pcbId].pcb,
        x: index * levelWidth + 1,
        y: baseY,
        width: pcbWidth,
        height: 0,
        color
      }
    });

    for (let i = 0; i < path.length; i++) {
      const pcbId = path[i];
      const toPcbId = path[i + 1];
      const connectors = sortedConnectors[pcbId];
      this.level = 1;

      for (let comp of [...connectors.right.keys()]) {
        const conns = connectors.right.get(comp);

        let currComp = {
          name: comp,
          x: layout.pcbs[pcbId].x + 3.5,
          y: layout.pcbs[pcbId].y + this.level + 0.5,
          width: compWidth,
          pins: [],
          dir: "right"
        }
        this.level += 0.5;
        this.setConnectorCompPosition({ layout, currComp, comp, conns, pcbId, toPcbId, edges });
      }
    }

    for (let pcbId of path) {
      const maxHeight = Array.from(layout.connections.keys()).filter(key => key.startsWith(pcbId)).reduce((max, key) => {
        const compHeight = layout.connections.get(key).height;
        const compY = layout.connections.get(key).y;

        return Math.max(max, compY + compHeight);
      }, 0);
      layout.pcbs[pcbId].height = maxHeight + 0.;
    }
    return layout
  }

  setConnectorCompPosition = ({ layout, currComp, comp, conns, pcbId, toPcbId, edges }) => {
    let height = 0;//top and bottom padding
    const pinSpacing = 1;
    const compWidth = 2;
    const pcbConns = [];
    const rightXSpacing = 0.74, leftXSpacing = 0.125;
    for (let conn of conns) {
      const [endPcbId, endComp] = conn.split('::');
      if (endPcbId !== toPcbId) {
        pcbConns.push({
          pcbId,
          comp,
          endPcbId,
          endComp,
        });
        continue;
      };
      let findEdge = edges.find(edge => edge.startPCB === pcbId && edge.start === comp && edge.endPCB === endPcbId && edge.end === endComp);
      let isChange = false;
      if (!findEdge) {
        findEdge = edges.find(edge => edge.startPCB === endPcbId && edge.start === endComp && edge.endPCB === pcbId && edge.end === comp);
        isChange = true;
      }
      if (!findEdge) {
        continue;
      }
      let toCompY = currComp.y;

      let toComp = layout.connections.has(`${endPcbId}::${endComp}`) ? { ...layout.connections.get(`${endPcbId}::${endComp}`) } : {
        name: endComp,
        x: layout.pcbs[endPcbId].x + 0.5,
        y: toCompY,
        width: compWidth,
        pins: [],
        dir: "left"
      }
      for (let pinConn of findEdge.pinsConnections) {
        const pinItem = isChange ? {
          pin: pinConn.end,
          net: pinConn.endNet,
          signal: pinConn.endSignal,
          x: currComp.x + compWidth - rightXSpacing,
          y: currComp.y + 1 + pinSpacing * currComp.pins.length
        } :
          {
            pin: pinConn.start,
            net: pinConn.startNet,
            signal: pinConn.startSignal,
            x: currComp.x + compWidth - rightXSpacing,
            y: currComp.y + 1 + pinSpacing * currComp.pins.length
          }
        const toPin = isChange ? {
          pin: pinConn.start,
          net: pinConn.startNet,
          signal: pinConn.startSignal,
          x: toComp.x + leftXSpacing,
          y: toComp.y + 1 + pinSpacing * currComp.pins.length
        } : {
          pin: pinConn.end,
          net: pinConn.endNet,
          signal: pinConn.endSignal,
          x: toComp.x + leftXSpacing,
          y: toComp.y + 1 + pinSpacing * currComp.pins.length
        }
        currComp.pins.push(pinItem);
        toComp.pins.push(toPin);
        layout.paths.push({
          from: `${comp}::${pinItem.pin}`,
          to: `${endComp}::${toPin.pin}`,
          signal: pinConn.startSignal,
          startX: pinItem.x + rightXSpacing,
          startY: pinItem.y + 0.25,
          endX: toPin.x - leftXSpacing,
          endY: toPin.y + 0.25,
        })
      };
      toComp.height = toComp.pins.length * 1 + 1.2;
      layout.connections.set(`${endPcbId}::${endComp}`, toComp);
      height = currComp.pins.length * 1 + 1.2;
    }

    for (let conn of pcbConns) {
      const { pcbId, comp, endPcbId, endComp } = conn;
      let findEdge = edges.find(edge => edge.startPCB === pcbId && edge.start === comp && edge.endPCB === endPcbId && edge.end === endComp);
      let isChange = false;
      if (!findEdge) {
        findEdge = edges.find(edge => edge.startPCB === endPcbId && edge.start === endComp && edge.endPCB === pcbId && edge.end === comp);
        isChange = true;
      }
      if (!findEdge) {
        continue;
      }
      let toCompY = currComp.y + height;
      let toComp = layout.connections.has(`${endPcbId}::${endComp}`) ? { ...layout.connections.get(`${endPcbId}::${endComp}`) } : {
        name: endComp,
        x: layout.pcbs[endPcbId].x + 0.5,
        y: toCompY,
        width: compWidth,
        pins: [],
        dir: "left"
      }
      for (let pinConn of findEdge.pinsConnections) {
        const pinItem = isChange ? {
          pin: pinConn.end,
          net: pinConn.endNet,
          signal: pinConn.endSignal,
          x: currComp.x + compWidth - rightXSpacing,
          y: currComp.y + 1 + pinSpacing * currComp.pins.length
        } : {
          pin: pinConn.start,
          net: pinConn.startNet,
          signal: pinConn.startSignal,
          x: currComp.x + compWidth - rightXSpacing,
          y: currComp.y + 1 + pinSpacing * currComp.pins.length
        }
        const toPin = isChange ? {
          pin: pinConn.start,
          net: pinConn.startNet,
          signal: pinConn.startSignal,
          x: toComp.x + 0.05,
          y: toComp.y + 1 + pinSpacing * currComp.pins.length
        } : {
          pin: pinConn.end,
          net: pinConn.endNet,
          signal: pinConn.endSignal,
          x: toComp.x + 0.05,
          y: toComp.y + 1 + pinSpacing * currComp.pins.length
        }
        layout.paths.push({
          from: `${comp}::${pinItem.pin}`,
          to: `${endComp}::${toPin.pin}`,
          signal: pinConn.startSignal,
          startX: pinItem.x + rightXSpacing,
          startY: pinItem.y + 0.25,
          endX: toPin.x - leftXSpacing,
          endY: toPin.y + 0.25,
        })
        currComp.pins.push(pinItem);
        toComp.pins.push(toPin);
      };
      toComp.height = toComp.pins.length * 1 + 1.2;
      layout.connections.set(`${endPcbId}::${endComp}`, toComp);
      height = currComp.pins.length * 1 + 1.2;
    }
    currComp.height = height;
    layout.connections.set(`${pcbId}::${comp}`, currComp);

    this.level += height;
  }

  // calculate Bounds
  calculateBounds = (layout) => {
    let maxX = 0, maxY = 0;

    for (let pos of Object.values(layout.pcbs)) {
      maxX = Math.max(maxX, pos.x + pos.width || 0);
      maxY = Math.max(maxY, pos.y + pos.height || 0);
    };
    layout.bounds = { width: maxX > 0 ? (maxX + 2) * this.unitSize : 0, height: maxY > 0 ? (maxY + 2) * this.unitSize : 0, unitSize: this.unitSize };
  }
}

const multiPCBConnections = new MultiPCBConnections();

export default multiPCBConnections;
